import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'react-toastify';
import type { Level } from '../../../api/types/Level';
import { useSubmission } from '../../../components/modals/useSubmission';
import { NumberInput } from '../../../components/shared/input/Input';
import { PrimaryButton } from '../../../components/ui/buttons';
import useSession from '../../../hooks/useSession';
import { clamp } from '../../../utils/clamp';
import Select from '../../../components/input/select/Select';
import LoadingSpinner from '../../../components/shared/LoadingSpinner';
import type { GetSingleSubmissionResponse } from '../../../api/submissions/GetSingleSubmission';
import { sendSubmission } from '../../../api/submissions/sendSubmission';
import renderToastError from '../../../utils/renderToastError';
import type { AxiosError } from 'axios';
import { useApp } from '../../../context/app/useApp';
import { Device } from '../../../api/core/enums/device.enum';
import { SubmissionStatus } from '@gdladder/shared/enums';

const statusOptions: Record<SubmissionStatus, string> = {
    beaten: 'Completed',
    beating: 'In progress',
    dropped: 'Dropped',
    hold: 'On hold',
    ptb: 'Plan to beat',
};

interface Props {
    level: Level;
}

export function QuickSubmit({ level }: Props) {
    const session = useSession();

    const query = useSubmission(level.ID, session.user?.ID, {
        enabled: session.user !== undefined,
    });

    if (!session.user) return;

    if (query.isPending) {
        return (
            <div className='round:rounded-lg bg-theme-700 border border-theme-outline p-2 my-2'>
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <QuickSubmitPresenter
            levelId={level.ID}
            userId={session.user.ID}
            submission={query.data}
            key={query.data?.ID}
        />
    );
}

interface QuickSubmitPresenterProps {
    levelId: number;
    userId: number;
    submission?: GetSingleSubmissionResponse;
}

function QuickSubmitPresenter({ levelId, userId, submission }: QuickSubmitPresenterProps) {
    const app = useApp();
    const [statusKey, setStatusKey] = useState<SubmissionStatus>(submission?.status ?? SubmissionStatus.PLAN_TO_BEAT);
    const [tier, setTier] = useState(submission?.Rating ?? null);
    const [enjoyment, setEnjoyment] = useState(submission?.Enjoyment ?? null);
    const [progress, setProgress] = useState(submission?.Progress ?? 0);
    const [progressKey, setProgressKey] = useState(0); // Used to re-mount the progress input should progress be updated by this component
    const queryClient = useQueryClient();

    const submitMutation = useMutation({
        mutationFn: sendSubmission,
        onMutate: () => toast.loading('Submitting...'),
        onSuccess: (data, _, toastId) => {
            void queryClient.invalidateQueries({ queryKey: ['level', levelId] });
            void queryClient.invalidateQueries({ queryKey: ['submission', levelId, userId] });
            void queryClient.invalidateQueries({ queryKey: ['user', userId] });

            toast.update(toastId, {
                render: data.wasAuto ? 'Submission accepted' : 'Submission queued',
                isLoading: false,
                autoClose: 5000,
                type: data.wasAuto ? 'success' : 'info',
            });
        },
        onError: (error: AxiosError, _, toastId) => renderToastError.error(toastId!, error),
    });

    const handleProgressChange = (value: number | null) => {
        if (value === null) return;
        const p = clamp(value, 0, 100);

        setProgress(p);
        if (p !== value) setProgressKey((prev) => prev + 1);

        if (p === 100) {
            setStatusKey(SubmissionStatus.BEATEN);
        } else if (p === 0) {
            setStatusKey(SubmissionStatus.PLAN_TO_BEAT);
            setTier(null);
            setEnjoyment(null);
        } else if (statusKey === SubmissionStatus.BEATEN) {
            setStatusKey(SubmissionStatus.BEATING);
        }
    };

    const handleStatusChange = (status: SubmissionStatus) => {
        setStatusKey(status);
        if (status === SubmissionStatus.BEATEN) {
            setProgress(100);
        } else if (status === SubmissionStatus.PLAN_TO_BEAT) {
            setTier(null);
            setEnjoyment(null);
            setProgress(0);
        } else {
            setProgress(clamp(progress, 1, 99));
        }
        setProgressKey((prev) => prev + 1);
    };

    const submit = () => {
        let submittedTier = tier;
        let submittedEnjoyment = enjoyment;
        let submittedProgress = progress;

        if (statusKey === SubmissionStatus.PLAN_TO_BEAT) {
            submittedTier = null;
            submittedEnjoyment = null;
            submittedProgress = 0;
        }

        submitMutation.mutate({
            levelID: levelId,
            rating: submittedTier,
            enjoyment: submittedEnjoyment,
            progress: submittedProgress,
            status: statusKey,
            refreshRate: submission?.RefreshRate ?? app.defaultRefreshRate ?? 60,
            device: submission?.Device ?? app.defaultDevice ?? Device.PC,
        });
    };

    return (
        <div className='round:rounded-xl bg-theme-700 border border-theme-outline p-2 my-2 flex justify-between flex-wrap'>
            <div className='flex flex-wrap gap-3 items-center'>
                <Select
                    id='submitStatus'
                    options={statusOptions}
                    key={statusKey}
                    onOption={handleStatusChange}
                    label={
                        <>
                            Status: <b className='ms-1'>{statusOptions[statusKey]}</b>
                        </>
                    }
                />
                {statusKey !== SubmissionStatus.PLAN_TO_BEAT && (
                    <>
                        <div className='flex items-center gap-2'>
                            <span>Tier:</span>
                            <div className='w-11'>
                                <NumberInput value={tier} onChange={setTier} disableSpinner={true} centered={true} />
                            </div>
                        </div>
                        <div className='flex items-center gap-2'>
                            <span>Enjoyment:</span>
                            <div className='w-11'>
                                <NumberInput
                                    value={enjoyment}
                                    onChange={setEnjoyment}
                                    disableSpinner={true}
                                    centered={true}
                                />
                            </div>
                        </div>
                        <div className='flex items-center'>
                            <span className='me-2'>Progress:</span>
                            <div className='w-14'>
                                <NumberInput
                                    value={progress}
                                    onChange={handleProgressChange}
                                    min={0}
                                    max={100}
                                    required
                                    disableSpinner={true}
                                    centered={true}
                                    key={progressKey}
                                />
                            </div>
                            <span className='ms-1'>%</span>
                        </div>
                    </>
                )}
            </div>
            <div className='ms-auto'>
                <Link to={`/submit/${levelId}`} className='me-2 text-sm link'>
                    advanced
                </Link>
                <PrimaryButton onClick={submit} loading={submitMutation.status === 'pending'}>
                    {submission ? 'Save' : 'Submit'}
                </PrimaryButton>
            </div>
        </div>
    );
}
